import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Check, X, User, Briefcase, FileText, Banknote,
  ShieldCheck, ShoppingCart, MessageSquare, Send, Package, Tag, Hash,
  BookOpen, ChevronDown, ChevronUp, Plus, Trash2, CheckCircle,
  Wallet, TrendingDown, TrendingUp, BarChart2, AlertTriangle, Download, Calendar
} from 'lucide-react';
import { getAuthHeader } from "../../helpers/auth"; 

const API = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────────────────────
// VOUCHER PDF GENERATOR  (jsPDF — browser side, no backend)
// ─────────────────────────────────────────────────────────────
const loadJsPDF = () =>
  new Promise((resolve, reject) => {
    if (window.jspdf) return resolve(window.jspdf.jsPDF);
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload  = () => resolve(window.jspdf.jsPDF);
    s.onerror = reject;
    document.head.appendChild(s);
  });

const generateVoucher = async (req, getJobName) => {
  const JsPDF = await loadJsPDF();
  const doc   = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const W      = 210;
  const mg     = 18;
  const lineH  = 10;
  let y        = 16;

  const tx  = (t, x, yy, opts = {}) => doc.text(String(t ?? ''), x, yy, opts);
  const ln  = (x1, y1, x2, y2)      => doc.line(x1, y1, x2, y2);
  const sf  = (style = 'normal', sz = 11) => { doc.setFont('helvetica', style); doc.setFontSize(sz); };

  // outer border
  doc.setDrawColor(0); doc.setLineWidth(0.5);
  doc.rect(mg - 4, 10, W - (mg - 4) * 2, 272);

  // ── Title ──
  sf('bold', 17);
  tx('PETTY CASH VOUCHER', W / 2, y + 6, { align: 'center' });
  y += 14;
  doc.setLineWidth(0.4); ln(mg, y, W - mg, y);
  y += 9;

  // ── Header fields ──
  const voucherNo  = `PCV-${String(req.id).padStart(4, '0')}`;
  const dateStr    = req.requested_at
    ? new Date(req.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const fields = [
    { label: 'Voucher No:',         value: voucherNo },
    { label: 'Date Requested:',     value: dateStr },
    { label: 'Department (Team):',  value: req.team_id || 'Operations' },
    { label: 'Requested By:',       value: req.requested_by || '—' },
    { label: 'Job / Site Name:',    value: getJobName(req.job_order_id, req.schedule_id) },
    { label: 'Reason for Request:', value: req.reason || '—' },
  ];

  fields.forEach(({ label, value }) => {
    sf('normal', 11); doc.setTextColor(0);
    tx(label, mg, y);
    const valX = mg + 48;
    tx(value, valX, y);
    doc.setLineWidth(0.25);
    ln(valX, y + 1.5, W - mg, y + 1.5);
    y += lineH;
  });

  y += 5;

  // ── Table ──
  const colDesc   = mg;
  const colQty    = mg + 90;
  const colRate   = mg + 118;
  const colAmt    = mg + 148;
  const tRight    = W - mg;
  const rowH      = 11;

  // header row
  sf('normal', 11);
  tx('Description', colDesc, y);
  tx('Quantity',    colQty,  y, { align: 'center' });
  tx('Rate',        colRate, y, { align: 'center' });
  tx('Amount',      colAmt,  y, { align: 'center' });
  y += 2;
  doc.setLineWidth(0.4); ln(mg, y, tRight, y); y += 1;

  // 4 rows (1 filled + 3 blank)
  const rows = [
    { d: req.reason || '—', q: '1', r: `Rs.${req.amount}`, a: `Rs.${req.amount}` },
    { d: '', q: '', r: '', a: '' },
    { d: '', q: '', r: '', a: '' },
    { d: '', q: '', r: '', a: '' },
  ];

  rows.forEach(row => {
    // vertical borders
    doc.setLineWidth(0.3);
    [colDesc - 1, colQty - 4, colRate - 4, colAmt - 4, tRight].forEach(cx => {
      ln(cx, y, cx, y + rowH);
    });
    // cell content
    sf('normal', 10);
    if (row.d) tx(row.d, colDesc + 1,  y + 7);
    if (row.q) tx(row.q, colQty,        y + 7, { align: 'center' });
    if (row.r) tx(row.r, colRate,       y + 7, { align: 'center' });
    if (row.a) tx(row.a, colAmt,        y + 7, { align: 'center' });
    y += rowH;
    ln(mg, y, tRight, y); // row bottom
  });

  // ── Total ──
  y += 7;
  sf('normal', 11);
  tx('Total Amount:', colAmt - 2, y, { align: 'right' });
  sf('bold', 11);
  tx(`Rs. ${req.amount}`, colAmt + 2, y);
  doc.setLineWidth(0.3);
  ln(colAmt - 4, y + 2, tRight, y + 2);

  // ── Signatures ──
  y += 22;
  const sigW = 48;
  const sigs = [
    { label: 'Requested By', x: mg },
    { label: 'Approved By',  x: mg + 60 },
    { label: 'Cashier',      x: mg + 120 },
  ];
  sf('normal', 11); doc.setTextColor(0);
  sigs.forEach(s => { tx(s.label, s.x, y); ln(s.x, y + 10, s.x + sigW, y + 10); });

  // ── Footer ──
  sf('normal', 8); doc.setTextColor(140);
  tx(
    `Generated on ${new Date().toLocaleString('en-GB')}  |  ${voucherNo}`,
    W / 2, y + 22, { align: 'center' }
  );

  doc.save(`PettyCash_${voucherNo}.pdf`);
};

// ─────────────────────────────────────────────────────────────
// PURCHASE REQUEST VOUCHER PDF GENERATOR
// ─────────────────────────────────────────────────────────────
const generatePurchaseVoucher = async (req) => {
  const JsPDF = await loadJsPDF();
  const doc   = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const W  = 210;
  const mg = 18;
  let y    = 16;

  const tx = (t, x, yy, opts = {}) => doc.text(String(t ?? ''), x, yy, opts);
  const ln = (x1, y1, x2, y2)      => doc.line(x1, y1, x2, y2);
  const sf = (style = 'normal', sz = 11) => { doc.setFont('helvetica', style); doc.setFontSize(sz); };

  // outer border
  doc.setDrawColor(0); doc.setLineWidth(0.5);
  doc.rect(mg - 4, 10, W - (mg - 4) * 2, 272);

  // ── Title ──
  sf('bold', 17);
  tx('PURCHASE REQUEST VOUCHER', W / 2, y + 6, { align: 'center' });
  y += 14;
  doc.setLineWidth(0.4); ln(mg, y, W - mg, y);
  y += 9;

  // ── Header fields ──
  const voucherNo = `PRV-${String(req.id).padStart(4, '0')}`;
  const dateStr   = req.requested_at
    ? new Date(req.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const fields = [
    { label: 'Voucher No:',         value: voucherNo },
    { label: 'Date Requested:',     value: dateStr },
    { label: 'Department:',         value: req.department || 'Operations' },
    { label: 'Requested By:',       value: req.requested_by || '—' },
    { label: 'Vendor Name:',        value: req.vendor || '—' },
    { label: 'Reason for Request:', value: req.reason || '—' },
  ];

  fields.forEach(({ label, value }) => {
    sf('normal', 11); doc.setTextColor(0);
    tx(label, mg, y);
    const valX = mg + 48;
    tx(value, valX, y);
    doc.setLineWidth(0.25);
    ln(valX, y + 1.5, W - mg, y + 1.5);
    y += 10;
  });

  y += 5;

  // ── Table ──
  const colDesc = mg;
  const colQty  = mg + 90;
  const colRate = mg + 118;
  const colAmt  = mg + 148;
  const tRight  = W - mg;
  const rowH    = 11;

  // header
  sf('normal', 11);
  tx('Description', colDesc, y);
  tx('Quantity',    colQty,  y, { align: 'center' });
  tx('Rate',        colRate, y, { align: 'center' });
  tx('Amount',      colAmt,  y, { align: 'center' });
  y += 2;
  doc.setLineWidth(0.4); ln(mg, y, tRight, y); y += 1;

  // rows
  const unitRate   = req.quantity && req.estimated_cost ? (parseFloat(req.estimated_cost) / parseFloat(req.quantity)).toFixed(2) : req.estimated_cost;
  const rows = [
    { d: req.item_name || '—', q: String(req.quantity || 1), r: `Rs.${unitRate}`, a: `Rs.${req.estimated_cost}` },
    { d: '', q: '', r: '', a: '' },
    { d: '', q: '', r: '', a: '' },
    { d: '', q: '', r: '', a: '' },
  ];

  rows.forEach(row => {
    doc.setLineWidth(0.3);
    [colDesc - 1, colQty - 4, colRate - 4, colAmt - 4, tRight].forEach(cx => ln(cx, y, cx, y + rowH));
    sf('normal', 10);
    if (row.d) tx(row.d, colDesc + 1, y + 7);
    if (row.q) tx(row.q, colQty + 7,      y + 7, { align: 'center' });
    if (row.r) tx(row.r, colRate + 7,      y + 7, { align: 'center' });
    if (row.a) tx(row.a, colAmt + 7,       y + 7, { align: 'center' });
    y += rowH;
    ln(mg, y, tRight, y);
  });

  if (req.priority) {
    y += 4;
    sf('normal', 10); doc.setTextColor(80);
    tx(`Priority: ${req.priority}`, mg, y);
    doc.setTextColor(0);
  }

  // ── Total ──
  y += 6;
  sf('normal', 11);
  tx('Total Amount:', colAmt - 2, y, { align: 'right' });
  sf('bold', 11);
  tx(`Rs. ${req.estimated_cost}`, colAmt + 2, y);
  doc.setLineWidth(0.3);
  ln(colAmt - 4, y + 2, tRight, y + 2);

  if (req.finance_comment) {
    y += 10;
    sf('normal', 9); doc.setTextColor(80);
    tx(`Finance Note: ${req.finance_comment}`, mg, y);
    doc.setTextColor(0);
  }

  // ── Signatures ──
  y += 20;
  const sigW = 48;
  const sigs = [
    { label: 'Requested By', x: mg },
    { label: 'Approved By',  x: mg + 60 },
    { label: 'Cashier',      x: mg + 120 },
  ];
  sf('normal', 11); doc.setTextColor(0);
  sigs.forEach(s => { tx(s.label, s.x, y); ln(s.x, y + 10, s.x + sigW, y + 10); });

  // ── Footer ──
  sf('normal', 8); doc.setTextColor(140);
  tx(`Generated on ${new Date().toLocaleString('en-GB')}  |  ${voucherNo}`, W / 2, y + 22, { align: 'center' });

  doc.save(`Purchase_Voucher_${voucherNo}.pdf`);
};

// ─────────────────────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────────────────────
const statusBadgeStyle = (status) => {
  const map = {
    Approved: { bg: 'rgba(34,211,165,0.1)', color: '#22D3A5', border: 'rgba(34,211,165,0.3)' },
    Rejected: { bg: 'rgba(248,113,113,0.1)', color: '#F87171', border: 'rgba(248,113,113,0.3)' },
  };
  const s = map[status] || { bg: 'rgba(245,158,11,0.1)', color: '#FBBF24', border: 'rgba(245,158,11,0.3)' };
  return { padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, letterSpacing: '0.05em', background: s.bg, color: s.color, border: `1px solid ${s.border}` };
};

const priorityColor = (p) => {
  if (p === 'Urgent') return { bg: 'rgba(239,68,68,0.12)', color: '#F87171', border: 'rgba(239,68,68,0.25)' };
  if (p === 'High')   return { bg: 'rgba(245,158,11,0.12)', color: '#FBBF24', border: 'rgba(245,158,11,0.25)' };
  return { bg: 'rgba(59,130,246,0.1)', color: '#60A5FA', border: 'rgba(59,130,246,0.2)' };
};

const addInputStyle = { width: '100%', height: 34, background: '#141927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7, color: '#E2E8F0', fontSize: 12, padding: '0 10px', outline: 'none', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box' };
const addBtnStyle = (color, bg) => ({ height: 34, padding: '0 12px', borderRadius: 7, background: bg, border: `1px solid ${color}40`, color, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap' });

// ─────────────────────────────────────────────────────────────
// LEDGER PANEL - 🔥 UPDATED TO PASS TRIGGER
// ─────────────────────────────────────────────────────────────
const LedgerPanel = ({ req, onLedgerUpdate }) => {
  const [open, setOpen]       = useState(false);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_received: 0, total_spent: 0, remaining: 0 });
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', amount: '' });
  const [adding, setAdding]   = useState(false);
  const [saving, setSaving]   = useState(false);

  const isApproved = req.status?.toLowerCase() === 'approved';

  useEffect(() => {
    if (!isApproved || !open) return;
    fetchLedger();
  }, [open, isApproved]);

  if (!isApproved) return null;

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/job-orders/finance/ledger/${req.id}`);
      if (res.data.success) {
        setEntries(res.data.data.entries);
        setSummary({ total_received: res.data.data.total_received, total_spent: res.data.data.total_spent, remaining: res.data.data.remaining });
      }
    } catch (err) { console.error('Ledger fetch:', err.message); }
    finally { setLoading(false); }
  };

  const addExpense = async () => {
    if (!newItem.description.trim() || !newItem.amount) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/job-orders/finance/ledger`, { request_id: req.id, description: newItem.description, amount: newItem.amount, added_by: 'finance' });
      if (res.data.success) { 
        setNewItem({ description: '', amount: '' }); 
        setAdding(false); 
        await fetchLedger(); 
        if(onLedgerUpdate) onLedgerUpdate(); // 🔥 Trigger FinanceSummary Update
      }
    } catch { alert('Expense save nahi hua.'); }
    finally { setSaving(false); }
  };

  const removeExpense = async (id) => {
    try { 
      await axios.delete(`${API}/job-orders/finance/ledger/${id}`); 
      await fetchLedger(); 
      if(onLedgerUpdate) onLedgerUpdate(); // 🔥 Trigger FinanceSummary Update
    }
    catch { alert('Delete nahi hua.'); }
  };

  const pct = summary.total_received > 0 ? Math.min((summary.total_spent / summary.total_received) * 100, 100) : 0;
  const pctColor = pct > 90 ? '#F87171' : pct > 65 ? '#FBBF24' : '#22D3A5';

  return (
    <div style={{ marginTop: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: open ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.28)', borderRadius: 8, padding: '6px 13px', color: '#60A5FA', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
        <BookOpen size={13} /> Team Ledger
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {entries.length > 0 && <span style={{ background: 'rgba(59,130,246,0.35)', borderRadius: 4, padding: '1px 7px', fontSize: 10 }}>{entries.length} entries</span>}
        {!open && summary.total_spent > 0 && <span style={{ marginLeft: 4, fontSize: 10, color: '#FBBF24', fontFamily: "'DM Mono', monospace" }}>Rs.{parseFloat(summary.total_spent).toLocaleString()} spent</span>}
      </button>

      {open && (
        <div style={{ marginTop: 8, background: '#06091A', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, overflow: 'hidden', animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              { label: 'Approved',  val: summary.total_received, color: '#22D3A5', icon: <Wallet size={12} /> },
              { label: 'Spent',     val: summary.total_spent,    color: '#FBBF24', icon: <TrendingDown size={12} /> },
              { label: 'Remaining', val: summary.remaining,      color: summary.remaining < 0 ? '#F87171' : '#60A5FA', icon: <BarChart2 size={12} /> },
            ].map(({ label, val, color, icon }, i) => (
              <div key={label} style={{ padding: '10px 14px', borderRight: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                  <span style={{ color }}>{icon}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(255,255,255,0.35)' }}>{label}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color, fontFamily: "'DM Mono', monospace" }}>Rs.{parseFloat(val || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
              <span>Budget utilisation</span><span style={{ color: pctColor, fontWeight: 800 }}>{pct.toFixed(0)}%</span>
            </div>
            <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 99 }}>
              <div style={{ height: '100%', width: `${pct}%`, background: pctColor, borderRadius: 99, transition: 'width 0.4s' }} />
            </div>
          </div>

          <div style={{ padding: '6px 14px 10px' }}>
            {loading ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '12px 0' }}>Loading...</div>
            ) : entries.length === 0 && !adding ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.22)', textAlign: 'center', padding: '10px 0', fontStyle: 'italic' }}>Abhi tak koi expense nahi.</div>
            ) : entries.map((entry, i) => (
              <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < entries.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <div>
                  <div style={{ fontSize: 12, color: '#CBD5E1', fontWeight: 600 }}>{entry.description}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 1 }}>
                    {new Date(entry.added_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {entry.added_by && <span style={{ marginLeft: 6, color: 'rgba(255,255,255,0.2)' }}>· {entry.added_by}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#FBBF24', fontFamily: "'DM Mono', monospace" }}>Rs.{parseFloat(entry.amount).toLocaleString()}</span>
                  <button onClick={() => removeExpense(entry.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'rgba(248,113,113,0.45)', display: 'flex' }}><Trash2 size={12} /></button>
                </div>
              </div>
            ))}

            {adding ? (
              <div style={{ marginTop: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 10, border: '1px solid rgba(59,130,246,0.18)' }}>
                <input placeholder="Expense description..." style={addInputStyle} value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} onKeyDown={e => e.key === 'Enter' && addExpense()} autoFocus />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input type="number" placeholder="Amount Rs." style={{ ...addInputStyle, flex: 1, fontFamily: "'DM Mono', monospace" }} value={newItem.amount} onChange={e => setNewItem({ ...newItem, amount: e.target.value })} onKeyDown={e => e.key === 'Enter' && addExpense()} />
                  <button onClick={addExpense} disabled={saving} style={addBtnStyle('#22D3A5', 'rgba(34,211,165,0.12)')}><CheckCircle size={13} /> {saving ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => { setAdding(false); setNewItem({ description: '', amount: '' }); }} style={addBtnStyle('#F87171', 'rgba(248,113,113,0.1)')}><X size={13} /></button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} style={{ marginTop: 8, width: '100%', padding: '7px 0', background: 'rgba(34,211,165,0.05)', border: '1px dashed rgba(34,211,165,0.2)', borderRadius: 8, color: '#22D3A5', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontFamily: "'DM Sans', sans-serif" }}>
                <Plus size={12} /> Log Expense
              </button>
            )}

            {summary.remaining < 0 && (
              <div style={{ marginTop: 10, padding: '7px 11px', borderRadius: 7, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', fontSize: 11, color: '#F87171', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={12} /> Overspent by Rs.{Math.abs(summary.remaining).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// FINANCE DYNAMIC SUMMARY - 🔥 FIXED TO FETCH LEDGERS
// ─────────────────────────────────────────────────────────────
const FinanceSummary = ({ pettyCashReqs, ledgerTrigger }) => {
  const [timeFilter, setTimeFilter] = useState('daily');
  const [allExpenses, setAllExpenses] = useState([]);

  const now = new Date();
  
  // Custom Time Filter Function
  const filterByTime = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    
    if (timeFilter === 'daily') {
      return date.toDateString() === now.toDateString();
    } else if (timeFilter === 'weekly') {
      const pastWeek = new Date();
      pastWeek.setDate(now.getDate() - 7);
      return date >= pastWeek && date <= now;
    } else if (timeFilter === 'monthly') {
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }
    return true;
  };

  // 🔥 Effect to load all expenses from approved requests
  useEffect(() => {
    const fetchAllLedgers = async () => {
      const approvedReqs = pettyCashReqs.filter(r => r.status?.toLowerCase() === 'approved');
      if (approvedReqs.length === 0) {
        setAllExpenses([]);
        return;
      }

      let expensesList = [];
      await Promise.all(
        approvedReqs.map(r =>
          axios.get(`${API}/job-orders/finance/ledger/${r.id}`)
            .then(res => {
              if (res.data.success && res.data.data.entries) {
                expensesList = [...expensesList, ...res.data.data.entries];
              }
            })
            .catch(() => {})
        )
      );
      setAllExpenses(expensesList);
    };

    fetchAllLedgers();
  }, [pettyCashReqs, ledgerTrigger]); // Runs when requests change OR when new ledger is added

  // Calculate based on time filter
  const filteredCashReqs = pettyCashReqs.filter(r => r.status?.toLowerCase() === 'approved' && filterByTime(r.requested_at));
  const filteredExpenses = allExpenses.filter(e => filterByTime(e.added_at));
  
  const totalDiya = filteredCashReqs.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const totalUseHua = filteredExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const totalRemaining = totalDiya - totalUseHua;

  const pct = totalDiya > 0 ? Math.min((totalUseHua / totalDiya) * 100, 100) : 0;
  const pctColor = pct > 90 ? '#F87171' : pct > 65 ? '#FBBF24' : '#22D3A5';

  const tiles = [
    { label: 'Total Sent (Approved)', val: totalDiya,      color: '#22D3A5', icon: <Wallet size={15} />,   sub: `${filteredCashReqs.length} requests` },
    { label: 'Total Use (Spent)', val: totalUseHua,    color: '#FBBF24', icon: <TrendingDown size={15} />, sub: `Budget Used: ${pct.toFixed(0)}%` },
    { label: 'Total Remaining',val: totalRemaining, color: totalRemaining < 0 ? '#F87171' : '#60A5FA', icon: <BarChart2 size={15} />, sub: 'Cash Flow Balance' },
  ];

  return (
    <div style={{ marginBottom: 28, background: '#0D1120', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
      <div style={{ padding: '14px 22px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 15 }}>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={16} color="#60A5FA" />
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Financial Overview</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#E2E8F0' }}>Petty Cash Summary</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', background: '#141927', padding: '4px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
          {[
            { id: 'daily', label: 'Daily' },
            { id: 'weekly', label: 'Weekly' },
            { id: 'monthly', label: 'Monthly' }
          ].map(f => (
            <button key={f.id} onClick={() => setTimeFilter(f.id)} 
              style={{ background: timeFilter === f.id ? '#1F2937' : 'transparent', color: timeFilter === f.id ? '#fff' : 'rgba(255,255,255,0.4)', border: 'none', padding: '6px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {tiles.map(({ label, val, color, icon, sub }, i) => (
          <div key={label} style={{ padding: '16px 20px', borderRight: i < tiles.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
              <span style={{ color }}>{icon}</span>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(255,255,255,0.35)' }}>{label}</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color, fontFamily: "'DM Mono', monospace", marginBottom: 2 }}>Rs.{val.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>{sub}</div>
            
            {label.includes('Use') && (
              <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, marginTop: 8 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: pctColor, borderRadius: 99, transition: 'width 0.4s' }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PETTY CASH CARD - 🔥 UPDATED TO PASS TRIGGER
// ─────────────────────────────────────────────────────────────
const PettyCashCard = ({ req, getJobName, formatTime, onStatus, onLedgerUpdate }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try { await generateVoucher(req, getJobName); }
    catch (e) { console.error(e); alert('Voucher generate nahi hua.'); }
    finally { setDownloading(false); }
  };

  return (
    <div style={{ background: '#0D1120', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Left info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Briefcase size={14} color="#60A5FA" /> {getJobName(req.job_order_id, req.schedule_id)}
            </div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: "'DM Mono', monospace" }}>
              {req.schedule_id ? `#SCH-${req.schedule_id}` : `#JO-${req.job_order_id}`}
            </span>
            {req.schedule_id && (
              <span style={{ background: 'rgba(59,130,246,0.2)', padding: '2px 6px', borderRadius: 4, fontSize: 9, color: '#60A5FA' }}>
                Commercial
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 15, fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <User size={12} /> {req.team_id}
              {req.requested_by && <span style={{ color: '#22D3A5', marginLeft: 4 }}>(By: {req.requested_by})</span>}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)' }}>{formatTime(req.requested_at)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, background: '#111827', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.03)' }}>
            <FileText size={14} color="rgba(255,255,255,0.3)" style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontStyle: req.reason ? 'normal' : 'italic', color: req.reason ? '#E2E8F0' : 'rgba(255,255,255,0.3)' }}>
              {req.reason || 'No details provided.'}
            </span>
          </div>
        </div>

        {/* Right: amount + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 30, minWidth: 170, gap: 8 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#F59E0B', fontFamily: "'DM Mono', monospace" }}>
            Rs.{req.amount}
          </div>

          {req.status === 'Pending' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => onStatus(req.id, 'Rejected')}
                style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                <X size={14} /> Reject
              </button>
              <button onClick={() => onStatus(req.id, 'Approved')}
                style={{ background: 'rgba(34,211,165,0.1)', border: '1px solid rgba(34,211,165,0.3)', color: '#22D3A5', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                <Check size={14} /> Approve
              </button>
            </div>
          ) : (
            <div style={statusBadgeStyle(req.status)}>{req.status.toUpperCase()}</div>
          )}

          {req.status === 'Approved' && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                background: downloading ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.35)',
                borderRadius: 9, padding: '9px 0',
                color: '#F59E0B', fontSize: 12, fontWeight: 700,
                cursor: downloading ? 'not-allowed' : 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all 0.2s',
              }}
            >
              <Download size={14} />
              {downloading ? 'Generating PDF...' : 'Download Voucher'}
            </button>
          )}
        </div>
      </div>

      {/* 🔥 Passing LedgerTrigger to LedgerPanel */}
      <LedgerPanel req={req} onLedgerUpdate={onLedgerUpdate} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PURCHASE CARD
// ─────────────────────────────────────────────────────────────
const PurchaseCard = ({ req, formatTime, onStatus, onComment }) => {
  const [commentText, setCommentText] = useState(req.finance_comment || '');
  const [showComment, setShowComment] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pc = priorityColor(req.priority);

  const handleDownload = async () => {
    setDownloading(true);
    try { await generatePurchaseVoucher(req); }
    catch (e) { console.error(e); alert('Voucher generate nahi hua.'); }
    finally { setDownloading(false); }
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(req.id, commentText);
    setShowComment(false);
    setSubmitting(false);
  };

  return (
    <div style={{ background: '#0D1120', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Package size={14} color="#F59E0B" /> {req.item_name || 'Unnamed Item'}
            </div>
            <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>
              {req.priority || 'NORMAL'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 15, fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Hash size={12} /> Qty: {req.quantity}</div>
            {req.vendor && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Tag size={12} /> {req.vendor}</div>}
            {req.requested_by && <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#22D3A5' }}><User size={12} /> By: {req.requested_by}</div>}
            <div style={{ color: 'rgba(255,255,255,0.3)' }}>{formatTime(req.requested_at)}</div>
          </div>
          {req.reason && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, background: '#111827', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.03)', marginBottom: 10 }}>
              <FileText size={14} color="rgba(255,255,255,0.3)" style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ color: '#E2E8F0' }}>{req.reason}</span>
            </div>
          )}
          {req.finance_comment && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', fontSize: 12, color: '#93C5FD', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <MessageSquare size={13} style={{ marginTop: 2, flexShrink: 0 }} />
              <div><span style={{ fontWeight: 700, marginRight: 6 }}>Finance note:</span>{req.finance_comment}</div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 30, minWidth: 150, gap: 8 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#F59E0B', fontFamily: "'DM Mono', monospace" }}>Rs.{req.estimated_cost}</div>
          {req.status === 'Pending' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => onStatus(req.id, 'Rejected')} style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 }}>
                <X size={13} /> Reject
              </button>
              <button onClick={() => onStatus(req.id, 'Approved')} style={{ background: 'rgba(34,211,165,0.1)', border: '1px solid rgba(34,211,165,0.3)', color: '#22D3A5', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 }}>
                <Check size={13} /> Approve
              </button>
            </div>
          ) : (
            <div style={statusBadgeStyle(req.status)}>{req.status.toUpperCase()}</div>
          )}

          {req.status === 'Approved' && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                background: downloading ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.35)',
                borderRadius: 9, padding: '9px 0',
                color: '#F59E0B', fontSize: 12, fontWeight: 700,
                cursor: downloading ? 'not-allowed' : 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all 0.2s',
              }}
            >
              <Download size={14} />
              {downloading ? 'Generating PDF...' : 'Download Voucher'}
            </button>
          )}

          <button onClick={() => setShowComment(!showComment)} style={{ background: showComment ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', color: '#60A5FA', padding: '7px 13px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700 }}>
            <MessageSquare size={12} /> {req.finance_comment ? 'Edit Comment' : 'Add Comment'}
          </button>
        </div>
      </div>

      {showComment && (
        <div style={{ marginTop: 14, padding: 14, background: '#111827', borderRadius: 10, border: '1px solid rgba(59,130,246,0.2)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Finance Comment</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <textarea placeholder="Add a note for the operations team..." value={commentText} onChange={(e) => setCommentText(e.target.value)}
              style={{ flex: 1, background: '#0D1120', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#E2E8F0', fontSize: 13, padding: '10px 12px', outline: 'none', resize: 'vertical', minHeight: 60, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}
            />
            <button onClick={handleCommentSubmit} disabled={submitting || !commentText.trim()}
              style={{ background: submitting || !commentText.trim() ? 'rgba(59,130,246,0.3)' : '#3B82F6', border: 'none', color: '#fff', borderRadius: 8, padding: '0 16px', cursor: submitting || !commentText.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, alignSelf: 'flex-end', height: 38 }}>
              <Send size={13} /> {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────────────────────
const FinanceDashboard = () => {
  const [activeTab, setActiveTab]         = useState('petty-cash');
  const [pettyCashReqs, setPettyCashReqs] = useState([]);
  const [purchaseReqs, setPurchaseReqs]   = useState([]);
  const [jobOrders, setJobOrders]         = useState([]);
  const [commercialJobs, setCommercialJobs] = useState([]); 
  const [statusFilter, setStatusFilter]   = useState('Pending');
  const [loading, setLoading]             = useState(true);
  const [ledgerTrigger, setLedgerTrigger] = useState(0); // 🔥 New State to trigger FinanceSummary Update

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);

      // 1. Fetch Residential Jobs
      try {
        let jobRes;
        try { jobRes = await axios.get(`${API}/job-orders/ops-job-orders`); }
        catch { jobRes = await axios.get(`${API}/active-dropdown`); }
        const raw = jobRes.data;
        let jobs = [];
        if (raw && Array.isArray(raw.data))      jobs = raw.data;
        else if (raw && Array.isArray(raw.rows)) jobs = raw.rows;
        else if (Array.isArray(raw))             jobs = raw;
        setJobOrders(jobs);
      } catch (e) { console.error('Job Orders error:', e); }

      // 2. Fetch Commercial Jobs
      try {
        const config = getAuthHeader();
        const storedUser = localStorage.getItem("user");
        let params = {};
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          const clientId = parsedUser.client_id || parsedUser.id;
          if (clientId) params.client_id = clientId;
        }
        const commRes = await axios.get(`${API}/schedule/history`, { params, ...config });
        if (commRes.data && Array.isArray(commRes.data.jobs)) {
          setCommercialJobs(commRes.data.jobs);
        }
      } catch (e) { console.error('Commercial jobs fetch error:', e); }

      // 3. Fetch Petty Cash
      try {
        const res = await axios.get(`${API}/job-orders/finance/petty-cash-all`);
        setPettyCashReqs(res.data.data || []);
      } catch (e) { console.error('Petty cash error:', e); }

      // 4. Fetch Purchase Requests
      try {
        const res = await axios.get(`${API}/purchase-request/get-requests`);
        setPurchaseReqs(res.data.data || []);
      } catch (e) { console.error('Purchase requests error:', e); }

      setLoading(false);
    };
    fetchAll();
  }, []);

  const handlePettyCashStatus = async (id, status) => {
    try {
      await axios.post(`${API}/job-orders/finance/petty-cash-status`, { id, status });
      setPettyCashReqs(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch { alert('Failed to update status.'); }
  };

  const handlePurchaseStatus = async (id, status) => {
    try {
      await axios.post(`${API}/purchase-request/set-approval`, { id, status });
      setPurchaseReqs(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch { alert('Failed to update status.'); }
  };

  const handlePurchaseComment = async (id, comment) => {
    try {
      await axios.post(`${API}/puchase-request/set-comment`, { id, comment });
      setPurchaseReqs(prev => prev.map(r => r.id === id ? { ...r, finance_comment: comment } : r));
    } catch { alert('Failed to save comment.'); }
  };

  const getJobName = (jobId, scheduleId) => {
    if (scheduleId) {
      const job = commercialJobs.find(j => (j.schedule_id || j.id) == scheduleId);
      return job ? (job.site_name || job.client_name || `Schedule-${scheduleId}`) : `Schedule-${scheduleId}`;
    }
    const j = jobOrders.find(o => (o.id || o.job_id) == jobId);
    return j ? (j.customer_name || j.client_name || `JO-${jobId}`) : `JO-${jobId}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredPetty    = pettyCashReqs.filter(r => statusFilter === 'All' || r.status === statusFilter);
  const filteredPurchase = purchaseReqs.filter(r => statusFilter === 'All' || r.status === statusFilter);
  const pendingPetty     = pettyCashReqs.filter(r => r.status === 'Pending').length;
  const pendingPurchase  = purchaseReqs.filter(r => r.status === 'Pending').length;

  return (
    <div style={{ padding: '40px 20px', background: '#080C18', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:99px}
      `}</style>

      <div style={{ maxWidth: '980px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#3B82F6', letterSpacing: '0.1em', marginBottom: 4 }}>FINANCE DEPARTMENT</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={28} color="#22D3A5" /> Finance Approvals
          </h1>
        </div>

        {/* 🔥 Updated Summary Component with trigger */}
        <FinanceSummary pettyCashReqs={pettyCashReqs} ledgerTrigger={ledgerTrigger} />

        {/* Tabs + Filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { key: 'petty-cash', label: 'Petty Cash',        icon: <Banknote size={15} />,    pending: pendingPetty,    color: '#22D3A5' },
            { key: 'purchase',   label: 'Purchase Requests', icon: <ShoppingCart size={15} />, pending: pendingPurchase, color: '#F59E0B' },
          ].map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setStatusFilter('Pending'); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12, background: activeTab === tab.key ? '#111827' : 'transparent', border: activeTab === tab.key ? `1px solid ${tab.color}40` : '1px solid rgba(255,255,255,0.08)', color: activeTab === tab.key ? '#fff' : 'rgba(255,255,255,0.45)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              <span style={{ color: activeTab === tab.key ? tab.color : 'rgba(255,255,255,0.3)' }}>{tab.icon}</span>
              {tab.label}
              {tab.pending > 0 && <span style={{ background: tab.color, color: '#080C18', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 900 }}>{tab.pending}</span>}
            </button>
          ))}

          <div style={{ marginLeft: 'auto', display: 'flex', background: '#111827', padding: '6px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)' }}>
            {['Pending', 'Approved', 'Rejected', 'All'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)} style={{ background: statusFilter === f ? '#1F2937' : 'transparent', color: statusFilter === f ? '#fff' : 'rgba(255,255,255,0.4)', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px', color: 'rgba(255,255,255,0.3)' }}>Loading requests...</div>
        ) : (activeTab === 'petty-cash' ? filteredPetty : filteredPurchase).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#0D1120', borderRadius: 16, border: '1px dashed rgba(255,255,255,0.1)' }}>
            {activeTab === 'petty-cash' ? <Banknote size={40} color="rgba(255,255,255,0.1)" style={{ marginBottom: 15 }} /> : <ShoppingCart size={40} color="rgba(255,255,255,0.1)" style={{ marginBottom: 15 }} />}
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>No {statusFilter} {activeTab === 'petty-cash' ? 'Petty Cash' : 'Purchase'} Requests</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {activeTab === 'petty-cash'
              ? filteredPetty.map(req => 
                  <PettyCashCard 
                    key={req.id} 
                    req={req} 
                    getJobName={getJobName} 
                    formatTime={formatTime} 
                    onStatus={handlePettyCashStatus} 
                    onLedgerUpdate={() => setLedgerTrigger(t => t + 1)} // 🔥 Pass Trigger
                  />
                )
              : filteredPurchase.map(req => 
                  <PurchaseCard 
                    key={req.id} 
                    req={req} 
                    formatTime={formatTime} 
                    onStatus={handlePurchaseStatus} 
                    onComment={handlePurchaseComment} 
                  />
                )
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceDashboard;